import React, { ReactElement, useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { Resolver, useForm } from 'react-hook-form';
import {
  IFormElementOptionProp,
  initialTwoFactorAuthFormValues,
  ITwoFactorAuthForm,
} from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSchema } from 'pages/TwoFactorAuthPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { useAuth } from 'models/authContext';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { Box } from '@mui/system';
import { AuthTemplate, StyledAuthHeader } from 'components/templates/AuthTemplate';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { v4 as uuidv4 } from 'uuid';
import { FC_SYSTEM_DEVICE_ID } from 'pages/LoginPage';
import { useTrustedDevice } from 'models/trustedDeviceContext';
import { ERouteLinks } from 'models/route';
import { useLocation, useNavigate } from 'react-router-dom';
import { IData } from 'pages/AuthPage';
import { IAuthResponse } from 'models/auth';
import { useAuthenticationSetup } from 'pages/SystemSettingsPage/AuthenticationSetupPage/hooks';
import { ItrustedDeviceOptions } from 'models/settings';
import { FormSelect } from 'components/molecules/FormSelect';
import { RouterLink } from 'components/atoms/RouterLink';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { Alert } from 'components/atoms/Alert';
import { getDeviceName } from 'helpers/userAgent';
import { useTranslations } from 'hooks/useTranslations';
import {
  StyledLanguageSelectOuterWrapper,
  StyledLanguageSelectWrapper,
} from 'components/templates/AuthTemplate/styles';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { AuthLogoContainer } from 'components/organisms/AuthLogoContainer';

export interface ITwoFactorAuthPageProps {
  email: string;
  password: string;
  phoneNumber: string;
  changeDataCallback: (p: IData) => void;
}

const TwoFactorAuthPage = ({
  email,
  password,
  phoneNumber,
  changeDataCallback,
}: ITwoFactorAuthPageProps): ReactElement => {
  const { schema } = useSchema();
  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: initialTwoFactorAuthFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });
  const navigate = useNavigate();
  const { t } = useTranslations();
  const preventRef = useRef(false);
  const { verifyAuthCode, logIn } = useAuth();
  const { state: routerState }: any = useLocation();
  const { addTrustedDevice } = useTrustedDevice();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isResendAuthCodeLoading, setIsResendAuthCodeLoading] = useState(false);
  const { getEnabledTrustedDeviceOptionsData, trustedDeviceOptions } =
    useAuthenticationSetup();

  useEffect(() => {
    getEnabledTrustedDeviceOptionsData();
  }, []);

  const getTrustedDeviceOptionId = () => {
    if (!trustedDeviceOptions?.length) return undefined;
    const option = trustedDeviceOptions.sort((a, b) => a.daysToExpire! - b.daysToExpire!);
    if (option.length) {
      return option[0].uniqueId;
    }
    return undefined;
  };

  const defaultValue: string | undefined = getTrustedDeviceOptionId();

  const mapTrustedDeviceOptionsToOptions = (
    options: ItrustedDeviceOptions[] | null,
  ): IFormElementOptionProp[] => {
    if (options === null) {
      return [];
    }
    return options.map((option) => ({
      label: option.daysToExpire ? option.daysToExpire.toString() : '0',
      value: option.uniqueId || '',
    }));
  };

  const onSubmit = handleSubmit(async (data) => {
    setIsLoginLoading(true);
    await verifyAuthCode(
      { email, enteredCodeValue: data.authCode, deviceName: getDeviceName() },
      () => {
        if (data.isRememberDevice) {
          const deviceToken = uuidv4();
          addTrustedDevice(
            {
              deviceToken,
              title: getDeviceName(),
              trustedDeviceTimeOptionUniqueId: data.trustedDeviceTimeOptionUniqueId,
            },
            () => {
              localStorage.setItem(FC_SYSTEM_DEVICE_ID, deviceToken);
            },
          );
        }
        navigate(ERouteLinks.Dashboard);
      },
      () => {
        setError('authCode', {
          type: 'custom',
          message: t('login.TwoFactorAuthenticationIncorrect.label'),
        });
      },
    );
    setIsLoginLoading(false);
  });

  const [formFields, setFormFields] = useState<ITwoFactorAuthForm>(
    initialTwoFactorAuthFormValues,
  );

  useEffect(() => {
    const subscription = watch(() => setFormFields(getValues() as any));
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (routerState?.isRedirectToTwoFactorAuthPage && !preventRef.current) {
      preventRef.current = true;
      logIn(
        {
          email,
          password: routerState?.password,
          deviceToken: localStorage.getItem(FC_SYSTEM_DEVICE_ID) || null,
          deviceName: getDeviceName(),
        },
        (res: IAuthResponse) => {
          if (res.phoneNumber) {
            changeDataCallback({
              email,
              password: routerState?.password,
              phoneNumber: res.phoneNumber,
              offlineQRCodeBase64: res?.offlineQRCodeBase64 ?? '',
            });
          }
        },
      );
    }
  }, []);

  return (
    <AuthTemplate>
      <Grid sx={{ py: 2, px: { xs: 2, xl: 20 }, justifyContent: "center", alignItems: "flex-start", alignContent: "stretch", pb: 5 }}>
        <Grid container>
          <StyledLanguageSelectOuterWrapper>
            <StyledLanguageSelectWrapper>
              <LanguageSelect isVisible />
            </StyledLanguageSelectWrapper>
          </StyledLanguageSelectOuterWrapper>
          <AuthLogoContainer />
        </Grid>
        <Grid sx={{ justifyContent: "center", alignItems: "flex-start" }}>
          {routerState?.isRedirectToTwoFactorAuthPage && (
            <Grid sx={{ mb: 6 }}>
              <Alert text={t('login.passwordChangedSuccessfully.label')} />
            </Grid>
          )}
          <Grid sx={{ mb: 3 }}>
            <StyledAuthHeader style={{ marginBottom: '16px' }}>
              {t('login.twoFactorAuthentication.label')}
            </StyledAuthHeader>
            {phoneNumber ? (
              <Typography variant="body1" sx={{ textAlign: "center", mb: 2, width: "370px" }}>
                {t('login.enterCodeSMS.label').replace('{0}', phoneNumber)}
              </Typography>
            ) : (
              <Typography variant="body1" sx={{ textAlign: "center", mb: 2 }}>
                {t('login.enterCodeEmail.label')}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box>
              <FormInput
                name="authCode"
                label={t('login.authenticationCode.label')}
                placeholder={t('login.enterReceivedCode.placeholder')}
                control={control}
                errors={errors}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: { xs: 32, sm: 40 },
                  },
                }}
              />
              {errors && errors.authCode && errors.authCode.type === 'custom' && (
                <Box sx={{ marginTop: '-25px' }}>
                  <FormErrorLabel label={errors.authCode.message as string} />
                </Box>
              )}
            </Box>
          </Grid>
          {trustedDeviceOptions?.length ? (
            <Grid sx={{ display: "flex", alignItems: "center" }}>
              <Box>
                <FormCheckbox
                  name="isRememberDevice"
                  label={t('login.rememberDevice.label')}
                  onChange={(value) => {
                    if (value) {
                      setValue('trustedDeviceTimeOptionUniqueId', defaultValue);
                    } else {
                      setValue('trustedDeviceTimeOptionUniqueId', '');
                    }
                  }}
                  control={control}
                  errors={errors}
                  withValidation={false}
                />
              </Box>
              <Box sx={{ height: "32px", marginTop: "-8px" }}>
                <FormSelect
                  name="trustedDeviceTimeOptionUniqueId"
                  options={mapTrustedDeviceOptionsToOptions(trustedDeviceOptions)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  disabled={!formFields.isRememberDevice}
                  withValidation={!formFields.isRememberDevice}
                  canBeEmpty={false}
                />
              </Box>
              <Typography variant="caption" sx={{ ml: 2 }}>
                {t('login.days.label')}
              </Typography>
            </Grid>
          ) : (
            ''
          )}
          <Grid sx={{ justifyContent: "center" }}>
            <Grid sx={{ mt: 3, mb: 1 }}>
              <Button
                label={t('login.verify.button')}
                variant={EButtonVariants.contained}
                onClick={() => onSubmit()}
                isLoading={isLoginLoading}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                label={t('login.resendAuthenticationCode.button')}
                variant={EButtonVariants.outlined}
                isLoading={isResendAuthCodeLoading}
                onClick={async () => {
                  setIsResendAuthCodeLoading(true);
                  await logIn({
                    email,
                    password,
                    deviceToken: localStorage.getItem(FC_SYSTEM_DEVICE_ID) || null,
                    deviceName: getDeviceName(),
                  });
                  setIsResendAuthCodeLoading(false);
                }}
                fullWidth
              />
            </Grid>
            <Grid sx={{ mt: 3, justifyContent: "center" }}>
              <RouterLink
                to={ERouteLinks.AuthWithSlash}
                label={t('login.backLogin.button')}
                replace
                forceRefresh
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthTemplate>
  );
};
export { TwoFactorAuthPage };
